import { type JSX, Show } from "solid-js";
import { AppBackground } from "@/components/app-background";
import { Link } from "@tanstack/solid-router";

/**
 * Custodia's OWN authentication shell — deliberately NOT the shared
 * `AuthLayout`/`AuthCard` frame inherited by clones (a centered glass card
 * inside a split branded panel). That common silhouette is exactly what made
 * products look like clones, so Custodia breaks it with its own identity.
 *
 * Custodia's identity is *family care and protection*, so its auth feels like
 * a welcoming HOME, not a cold registration form:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ ◈ Custodia              [ warm eyebrow ]     │ ← brand header
 *   │                                                │
 *   │  ╭────────────────────────────────────────╮   │
 *   │  ▏ Welcoming headline.                      │  │ ← rounded surface with
 *   │  ▏ Soothing supporting tagline.             │  │   soft accent rail
 *   │  ┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄  │  │
 *   │  ▏ email  [____________________]            │  │ ← form on a
 *   │  ▏ pass   [____________________]            │  │   warm surface (rounded
 *   │  ▏ [ Sign In ]                              │  │   card, soft shadow)
 *   │  ╰────────────────────────────────────────╯   │
 *   └──────────────────────────────────────────────┘
 *
 * The shell is agnostic regarding the *form*: each screen renders its
 * fields as `children`.
 *
 * ── SCROLL CONTRACT (kit #13) ──────────────────────────────────────────────
 * `AppBackground` is `position: fixed; overflow: hidden` and clips its children,
 * so here it is used only as a SIBLING decorative background. The content lives in
 * its own scroll container: since global CSS sets
 * `html, body { overflow: hidden; height: 100% }` (to suppress portal scrollbars),
 * we bound the container to the viewport (`h-[100dvh]`) so its
 * OWN `overflow-y-auto` activates and the card scrolls internally when it doesn't
 * fit (portrait or short landscape). The inner `min-h-full justify-center`
 * keeps centering a short form, and the button is never clipped.
 */

interface AuthSceneProps {
  /** Warm chip above header (e.g. "Welcome back"). Theme-safe. */
  eyebrow?: string;
  /** Screen headline. Optional for headerless states. */
  headline?: string;
  /** Supporting phrase below headline. */
  tagline?: string;
  /**
   * Heading level of headline. In Custodia auth the headline IS the h1
   * of the page. Pass "h2" only if the screen has its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** Card width. "narrow" for login/forgot/reset/verify, "wide" for register. */
  width?: "narrow" | "wide";
  /** The form (fields + actions), on the warm surface. */
  children: JSX.Element;
  /** Footer slot (links: register / back to home), below form. */
  footer?: JSX.Element;
}

/** Custodia brand mark: protective shield with heart, over a solid accent chip. */
function MarkChip() {
  return (
    <span
      class="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95"
      aria-hidden="true"
    >
      <svg
        class="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.9"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M12 3.2l6.5 2.3v5.2c0 4.3-2.8 7.6-6.5 9.1-3.7-1.5-6.5-4.8-6.5-9.1V5.5L12 3.2Z" />
        <path
          d="M12 15.4c-1.9-1.3-3.3-2.5-3.3-4a1.85 1.85 0 0 1 3.3-1.15A1.85 1.85 0 0 1 15.3 11.4c0 1.5-1.4 2.7-3.3 4Z"
          fill="currentColor"
          stroke="none"
        />
      </svg>
    </span>
  );
}

export function AuthScene(props: AuthSceneProps) {
  return (
    <>
      {/* Fixed decorative background. NEVER the scroll container. */}
      <AppBackground />

      {/* Real scroll container (see contract above). */}
      <div
        class="relative z-10 h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-4 sm:px-6"
        style={{
          "padding-top": "max(1.5rem, env(safe-area-inset-top))",
          "padding-bottom": "max(1.5rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1rem, env(safe-area-inset-left))",
          "padding-right": "max(1rem, env(safe-area-inset-right))",
        }}
      >
        <div
          class="mx-auto flex min-h-full w-full flex-col justify-center py-6"
          classList={{
            "max-w-md": (props.width ?? "narrow") === "narrow",
            "max-w-lg": props.width === "wide",
          }}
        >
          {/* Brand header — shield chip + wordmark + warm eyebrow chip. */}
          <div class="mb-5 flex items-center justify-between gap-3 px-1">
            <Link
              to="/"
              class="group flex items-center gap-2.5 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label="Custodia"
            >
              <MarkChip />
              <span class="text-lg font-extrabold tracking-tight text-foreground">
                Custod<span class="text-accent">ia</span>
              </span>
            </Link>
            <Show when={props.eyebrow}>
              <span class="inline-flex items-center gap-1.5 rounded-full bg-accent/12 px-3 py-1 text-[0.72rem] font-bold tracking-wide text-accent ring-1 ring-accent/20">
                <span class="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
                {props.eyebrow}
              </span>
            </Show>
          </div>

          {/* Warm surface: rounded card with soft accent rail on top. */}
          <div class="overflow-hidden rounded-3xl border border-border/70 bg-surface/85 shadow-[var(--overlay-shadow)] backdrop-blur-sm">
            <div
              class="h-1.5 w-full bg-gradient-to-r from-accent/70 via-accent/40 to-transparent"
              aria-hidden="true"
            />
            <div class="p-6 sm:p-8">
              <Show when={props.headline}>
                <div class="space-y-2">
                  <Show
                    when={(props.headingLevel ?? "h1") === "h2"}
                    fallback={
                      <h1 class="text-pretty text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                        {props.headline}
                      </h1>
                    }
                  >
                    <h2 class="text-pretty text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                      {props.headline}
                    </h2>
                  </Show>
                  <Show when={props.tagline}>
                    <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                      {props.tagline}
                    </p>
                  </Show>
                </div>
                <div class="my-6 h-px w-full bg-border/60" />
              </Show>

              {/* Form lives on top of warm surface. */}
              <div class="w-full">{props.children}</div>

              <Show when={props.footer}>
                <footer class="mt-6 space-y-3 text-sm">{props.footer}</footer>
              </Show>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
